import { DayOfWeek } from "./constants";

interface Availability {
  dayOfWeek: DayOfWeek;
  start: number; // in milliseconds
  end: number; // in milliseconds
}

// The type that matches createInterviewSettingsFormSchema.timeSlots items
interface TimeSlot {
  dayOfWeek: DayOfWeek;
  isAvailable: boolean;
  slots: Array<{
    startTime: string;
    endTime: string;
  }>;
}

export function formatTime(time: string): string {
  const date = new Date(`1970-01-01T${time}`);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function hasOverlappingTimeSlots(slots: Array<{ startTime: string; endTime: string }>) {
  for (let index = 0; index < slots.length; index++) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    for (let index_ = index + 1; index_ < slots.length; index_++) {
      const slot1 = slots[index];
      const slot2 = slots[index_];

      const start1 = new Date(`1970-01-01T${slot1.startTime}`);
      const end1 = new Date(`1970-01-01T${slot1.endTime}`);
      const start2 = new Date(`1970-01-01T${slot2.startTime}`);
      const end2 = new Date(`1970-01-01T${slot2.endTime}`);

      if (start1 < end2 && start2 < end1) {
        return {
          hasOverlap: true,
          message: `Time interval ${formatTime(slot1.startTime)}-${formatTime(
            slot1.endTime
          )} should not overlap with ${formatTime(slot2.startTime)}-${formatTime(slot2.endTime)}`,
        };
      }
    }
  }

  return { hasOverlap: false, message: "" };
}

export function validateStartBeforeEnd(slot: { startTime: string; endTime: string }) {
  const start = new Date(`1970-01-01T${slot.startTime}`);
  const end = new Date(`1970-01-01T${slot.endTime}`);

  if (start >= end) {
    return {
      isValid: false,
      message: `Invalid time range: Start time ${formatTime(
        slot.startTime
      )} can't be later than end time ${formatTime(slot.endTime)}`,
    };
  }

  return { isValid: true, message: "" };
}

// A helper to format milliseconds into "HH:mm"
function formatToHHmm(milliseconds: number): string {
  // Create a date at midnight UTC
  const baseDate = new Date(0);
  // Add the milliseconds to get the time offset
  baseDate.setUTCMilliseconds(milliseconds);

  const hours = baseDate.getUTCHours().toString().padStart(2, "0");
  const minutes = baseDate.getUTCMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function transformAvailabilityToTimeSlots(availability: Availability[]): TimeSlot[] {
  // We explicitly list days in the order we want them in the final array:
  const daysInOrder: DayOfWeek[] = [
    DayOfWeek.SUNDAY,
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
  ];

  // Build an object keyed by dayOfWeek for quick grouping
  const availabilityByDay = daysInOrder.reduce<Record<DayOfWeek, Availability[]>>(
    (accumulator, day) => {
      accumulator[day] = [];
      return accumulator;
    },

    {} as Record<DayOfWeek, Availability[]>
  );

  // Group availability items by their dayOfWeek
  availability.forEach((item) => {
    availabilityByDay[item.dayOfWeek].push(item);
  });

  // Now transform into the desired array of timeSlots
  const timeSlots: TimeSlot[] = daysInOrder.map((day) => {
    const dayAvailability = availabilityByDay[day];

    if (dayAvailability.length === 0) {
      // No availability for this day
      return {
        dayOfWeek: day,
        isAvailable: false,
        slots: [],
      };
    }

    // We have at least one availability entry for this day
    return {
      dayOfWeek: day,
      isAvailable: true,
      slots: dayAvailability.map((slot) => ({
        startTime: formatToHHmm(slot.start),
        endTime: formatToHHmm(slot.end),
      })),
    };
  });

  return timeSlots;
}

/**
 * Helper that converts "HH:mm" to a millisecond offset from 00:00.
 */
export function parseHHmmToMs(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 * 60 * 1000 + minutes * 60 * 1000;
}

/**
 * Transforms timeSlots (createInterviewSettingsFormSchema) back into
 * an availability array (interviewSettingsAttributesSchema).
 * This version uses today's local date for each slot.
 */
export function transformTimeSlotsToAvailability(timeSlots: TimeSlot[]): Availability[] {
  const availability: Availability[] = [];

  for (const daySlot of timeSlots) {
    // Only if this day is marked "isAvailable" do we create entries
    if (daySlot.isAvailable) {
      for (const slot of daySlot.slots) {
        availability.push({
          dayOfWeek: daySlot.dayOfWeek,
          start: parseHHmmToMs(slot.startTime),
          end: parseHHmmToMs(slot.endTime),
        });
      }
    }
  }

  return availability;
}
